import type { RouteRecordRaw } from 'vue-router'

export const routes: RouteRecordRaw[] = [
  {
    path: '/setup',
    name: 'Setup',
    component: () => import('@/views/auth/SetupView.vue'),
    meta: { requiresAuth: false, isSetup: true },
  },
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/views/auth/LoginView.vue'),
    meta: { requiresAuth: false },
  },
  {
    path: '/auth/sso',
    name: 'SsoCallback',
    component: () => import('@/views/auth/SsoCallback.vue'),
    meta: { requiresAuth: false },
  },
  {
    path: '/',
    name: 'overview',
    component: () => import('@/views/Overview.vue'),
    meta: { nav: { section: '概览分析', label: '安全概览', icon: 'dashboard', order: 1 }, requiresAuth: true },
  },
  {
    path: '/analytics',
    name: 'analytics',
    component: () => import('@/views/Analytics.vue'),
    meta: { nav: { section: '概览分析', label: '数据分析', icon: 'analytics', order: 2 }, requiresAuth: true, requiredRoles: ['admin', 'tenant_admin'] },
  },
  {
    path: '/policies',
    name: 'policies',
    component: () => import('@/views/Policies.vue'),
    meta: { nav: { section: '策略治理', label: '策略管理', icon: 'policy', order: 1 }, requiresAuth: true, requiredRoles: ['admin', 'tenant_admin'] },
  },
  {
    // 项目治理（CLO2-3）：原「项目密级」+「SCM 接入」合并为统一页四个 tab
    path: '/governance',
    name: 'governance',
    component: () => import('@/views/UnifiedProjectGovernance.vue'),
    meta: { nav: { section: '策略治理', label: '项目治理', icon: 'lock', order: 3 }, requiresAuth: true, requiredRoles: ['admin', 'tenant_admin'] },
  },
  {
    // 旧入口重定向到新页面对应 tab（深链兼容）
    path: '/projects',
    redirect: { path: '/governance', query: { tab: 'projects' } },
  },
  {
    path: '/attribution/scm-integrations',
    redirect: { path: '/governance', query: { tab: 'scm' } },
  },
  {
    path: '/detectors',
    name: 'detectors',
    component: () => import('@/views/Detectors.vue'),
    meta: { nav: { section: '策略治理', label: '检测器', icon: 'eye', order: 2 }, requiresAuth: true, requiredRoles: ['admin', 'tenant_admin'] },
  },
  {
    // 敏感资产库（相似度比对 Phase 1）：文件上传 / 切片预览 / 配额设置。
    // order 3 与「项目治理」同号，排在「检测器」之后（不重排其他路由）。
    path: '/asset-library',
    name: 'asset-library',
    component: () => import('@/views/AssetLibrary.vue'),
    meta: { nav: { section: '策略治理', label: '敏感资产库', icon: 'document', order: 3 }, requiresAuth: true, requiredRoles: ['admin', 'tenant_admin'] },
  },
  {
    // 旧入口重定向：内容安全词库已并入「检测器」页的 tab（深链兼容），
    // 平台 admin 校验由目标页 tab 显隐与后端 require_platform_admin 保证。
    path: '/content-safety',
    redirect: { path: '/detectors', query: { tab: 'content-safety' } },
  },
  {
    path: '/events',
    name: 'events',
    component: () => import('@/views/SecurityEvents.vue'),
    meta: { nav: { section: '审计取证', label: '安全事件', icon: 'shield', order: 1 }, requiresAuth: true },
  },
  {
    path: '/audit',
    name: 'audit',
    component: () => import('@/views/AuditLogs.vue'),
    meta: { nav: { section: '审计取证', label: '审计日志', icon: 'document', order: 2 }, requiresAuth: true },
  },
  {
    // 审计留痕：两个标签页 —— 访问留痕（谁查看过审计内容，CLO2-71）
    // 与管理操作（谁改了配置，admin_audit_logs）。
    //
    // 菜单名刻意是「审计留痕」而不是「访问留痕」：这个页面现在同时承载「谁看了」
    // 和「谁改了」两件事，叫「访问留痕」会让人以为配置变更不在这儿 —— 而用户
    // 确实是在这个页面找「我改的通知渠道怎么没留痕」才发现那张表压根没建的。
    //
    // 挂在「审计取证」而不是「平台管理」，因为它回答的是取证链条上的下一个问题
    // （「这条审计被谁看过 / 这条规则被谁改过」），与上面两项同一场景。admin-only
    // —— 留痕里带着其他租户操作人的邮箱、来源 IP 与配置全貌，后端各端点也各自硬门。
    path: '/audit-access-logs',
    name: 'audit-access-logs',
    component: () => import('@/views/AuditAccessLogs.vue'),
    meta: { nav: { section: '审计取证', label: '审计留痕', icon: 'finger-print', order: 3 }, requiresAuth: true, requiredRoles: ['admin'] },
  },
  {
    // 旧入口重定向：「日志检索」页已下线（深链兼容）。
    //
    // 该页是「审计日志」页的严格子集——同一个 useAuditLogs composable、同一个
    // GET /api/v1/audit-logs、同一张 call_logs + call_log_details、同一个
    // AuditLogDetail 抽屉，连 Header / Body 检索都是同一个 HeaderSearchPanel
    // 组件。它唯一独有的能力（「字节同源取证」Tab）已于 2026-05-30 下线，此后
    // 本页只剩「筛选器少一半的审计日志」，故 2026-09-08 整页删除。
    // 审计日志页额外提供导出 / 归档 / 批量删除 / 全量筛选，无能力丢失。
    path: '/forensics',
    redirect: { path: '/audit' },
  },
  {
    // 异常调用行为（docs/dev/guardrail-call-anomaly/design.md §6）。
    //
    // 挂「平台管理」而不是「审计取证」：它不是取证链条上的一环（不产 finding、
    // 不产 verdict、不碰 sec_verdict），而是跨租户的运营排查视图——一张表里同时
    // 列着多个租户的主体与邮箱，所以 admin-only，tenant_admin 根本不该进来。
    path: '/call-anomaly',
    name: 'call-anomaly',
    component: () => import('@/views/CallAnomaly.vue'),
    meta: { nav: { section: '平台管理', label: '异常调用行为', icon: 'scan', order: 6 }, requiresAuth: true, requiredRoles: ['admin'] },
  },
  {
    path: '/settings',
    name: 'settings',
    component: () => import('@/views/Settings.vue'),
    meta: { nav: { section: '平台管理', label: '系统设置', icon: 'settings', order: 4 }, requiresAuth: true, requiredRoles: ['admin'] },
  },
  {
    path: '/users',
    name: 'users',
    component: () => import('@/views/Users.vue'),
    meta: { nav: { section: '平台管理', label: '用户管理', icon: 'users', order: 2 }, requiresAuth: true, requiredRoles: ['admin'] },
  },
  {
    path: '/tenants',
    name: 'tenants',
    component: () => import('@/views/TenantManagement.vue'),
    meta: { nav: { section: '平台管理', label: '租户管理', icon: 'tenant', order: 1 }, requiresAuth: true, requiredRoles: ['admin'] },
  },
  {
    path: '/tenants/:id',
    name: 'tenant-detail',
    component: () => import('@/views/TenantDetailView.vue'),
    meta: { requiresAuth: true, requiredRoles: ['admin'] },
  },
  {
    path: '/workspace',
    name: 'workspace',
    component: () => import('@/views/WorkspaceSettings.vue'),
    meta: { nav: { section: '平台管理', label: '空间设置', icon: 'workspace', order: 5 }, requiresAuth: true, requiredRoles: ['tenant_admin'] },
  },
  {
    path: '/upstream-channels',
    name: 'upstream-channels',
    component: () => import('@/views/UpstreamChannels.vue'),
    meta: { nav: { section: '接入与流量', group: '流量治理', label: '上游通道', icon: 'upstream', order: 3 }, requiresAuth: true, requiredRoles: ['admin'] },
  },
  {
    // 模型资源可信度（design-resource-trust.md）：检测上游响应的协议面特征，
    // 发现通道可能并非直连官方。单页 3 Tab，?tab= 支持深链（告警通知直达）。
    // 仅平台超管：通道是平台资产，租户看到「你用的通道有异常」会变成客诉。
    path: '/resource-trust',
    name: 'resource-trust',
    component: () => import('@/views/ResourceTrust.vue'),
    meta: { nav: { section: '接入与流量', group: '流量治理', label: '资源可信度', icon: 'shield', order: 7 }, requiresAuth: true, requiredRoles: ['admin'] },
  },
  {
    path: '/traffic-rewrite',
    name: 'traffic-rewrite',
    component: () => import('@/views/TrafficRewrite.vue'),
    meta: { nav: { section: '接入与流量', group: '流量治理', label: '流量改写', icon: 'rewrite', order: 5 }, requiresAuth: true, requiredRoles: ['admin'] },
  },
  {
    path: '/forward-proxy',
    name: 'forward-proxy',
    component: () => import('@/views/ForwardProxy.vue'),
    meta: { nav: { section: '接入与流量', group: '流量治理', label: 'Forward Proxy', icon: 'forward', order: 4 }, requiresAuth: true, requiredRoles: ['admin'] },
  },
  {
    path: '/stream-prewarm',
    name: 'stream-prewarm',
    component: () => import('@/views/StreamPrewarm.vue'),
    meta: { nav: { section: '接入与流量', group: '流量治理', label: '首字节预热', icon: 'bolt', order: 6 }, requiresAuth: true, requiredRoles: ['admin'] },
  },
  {
    path: '/quickstart',
    name: 'quickstart',
    component: () => import('@/views/QuickStart.vue'),
    meta: { nav: { section: '接入与流量', group: '接入', label: '快速接入', icon: 'rocket', order: 1 }, requiresAuth: true, requiredRoles: ['admin'] },
  },
  {
    path: '/ai-credentials',
    name: 'ai-credentials',
    component: () => import('@/views/AIAccessCredentials.vue'),
    meta: { nav: { section: '接入与流量', group: '接入', label: 'AI 访问凭据', icon: 'key', order: 2 }, requiresAuth: true, requiredRoles: ['admin'] },
  },
  {
    path: '/resources',
    name: 'resources',
    component: () => import('@/views/ResourceManagement.vue'),
    meta: { nav: { section: '平台管理', label: '资源管理', icon: 'resource', order: 3 }, requiresAuth: true, requiredRoles: ['admin'] },
  },
  {
    // 归档监控独立页：仅深链入口，不上菜单。
    // 功能入口已收进「系统设置 → 长期归档」（同一套区块组件复用），
    // 菜单重复入口曾在 dca89bd12 删除，勿再加回 meta.nav。
    path: '/archival-jobs',
    name: 'archival-jobs',
    component: () => import('@/views/ArchivalJobs.vue'),
    meta: { requiresAuth: true, requiredRoles: ['admin'] },
  },
  // ─── Ops Alert 告警引擎已于 2026-08-27 整体移除 ───
  // 运营类监控/告警统一收口 OpsCenter，Guardrail 不再自建（产品边界决策）。
  // 原 /ops-alert/{overview,rules,series,events} 四个路由与后端 ops_alert 模块、
  // ops_alert_{rules,series,events} 三张表一并删除。勿再加回。
  // ─── Notification Channels / Templates / DispatchLog（仅 admin） ───
  {
    path: '/notification/channels',
    name: 'notification-channels',
    component: () => import('@/views/NotificationChannels.vue'),
    meta: { nav: { section: '告警通知', group: '通知', label: '通知渠道', icon: 'megaphone', order: 5 }, requiresAuth: true, requiredRoles: ['admin'] },
  },
  {
    path: '/notification/templates',
    name: 'notification-templates',
    component: () => import('@/views/NotificationTemplates.vue'),
    meta: { nav: { section: '告警通知', group: '通知', label: '通知模板', icon: 'mail', order: 6 }, requiresAuth: true, requiredRoles: ['admin'] },
  },
  {
    path: '/notification/dispatch-log',
    name: 'notification-dispatch-log',
    component: () => import('@/views/NotificationDispatchLog.vue'),
    meta: { nav: { section: '告警通知', group: '通知', label: '通知日志', icon: 'paper-airplane', order: 7 }, requiresAuth: true, requiredRoles: ['admin'] },
  },
]
